import { useState, useEffect, useMemo } from 'react';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { useSubscription } from '../../../../contexts/SubscriptionContext';

/**
 * Hook personalizado para gerenciar os dados da assinatura
 * Inclui detalhes da assinatura, histórico de pagamentos e histórico de alterações
 */
const useSubscriptionData = () => {
  // Estados para armazenar os dados
  const [subscription, setSubscription] = useState(null);
  const [addons, setAddons] = useState([]);
  const [payments, setPayments] = useState([]);
  const [history, setHistory] = useState([]);
  const [plans, setPlans] = useState({});
  // Próxima cobrança pela prévia do Stripe: { nextChargeCents, creditCents } ou null
  const [nextCharge, setNextCharge] = useState(null);
  
  // Estados para paginação e filtros
  const [paymentsPagination, setPaymentsPagination] = useState(null);
  const [historyPagination, setHistoryPagination] = useState(null);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Estados para controle de carregamento e erros
  const [loading, setLoading] = useState({
    subscription: true,
    payments: true,
    history: true,
    plans: true
  });
  const [error, setError] = useState(null);
  
  // Obter funções do DataProvider
  const {
    GetCompanySubscription,
    GetSubscriptionPaymentHistory,
    GetSubscriptionHistory,
    GetSubscriptionPlans,
    CancelCompanySubscription,
    CancelSpecificSubscription,
    CreateStripeCustomerPortal,
    SimulatePlanChange
  } = DataProviderInstance();
  
  // Efeito para carregar os detalhes da assinatura
  useEffect(() => {
    const fetchSubscription = () => {
      setLoading(prev => ({ ...prev, subscription: true }));
      
      GetCompanySubscription((result) => {
        setLoading(prev => ({ ...prev, subscription: false }));
        
        if (result.success) {
          setSubscription(result.subscription);
          setAddons(result.addons || []);
          setError(null);
        } else {
          setError('Erro ao carregar os detalhes da assinatura. Por favor, tente novamente mais tarde.');
          
        }
      });
    };
    
    fetchSubscription();
  // Só ao montar: a função do DataProvider não é estável e refazia a chamada em loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Prévia da próxima cobrança (valor já com o crédito de uma troca para plano menor).
  // Só com assinatura ativa no Stripe; sem prévia, a tela segue sem o valor.
  const nextChargePlanId = subscription?.subscription_status?.toLowerCase() === 'active'
    && subscription?.stripe_subscription_id
    ? subscription?.subscription_plan_id
    : null;
  useEffect(() => {
    setNextCharge(null);
    if (!nextChargePlanId) return undefined;
    let active = true;
    SimulatePlanChange(nextChargePlanId, true, 'now', (result) => {
      if (!active) return;
      const sim = result?.success ? result.simulation : null;
      if (!sim || typeof sim.next_charge_cents !== 'number') return;
      setNextCharge({
        nextChargeCents: sim.next_charge_cents,
        creditCents: typeof sim.credit_cents === 'number' ? sim.credit_cents : 0,
      });
    });
    return () => { active = false; };
  // Só refaz quando muda o plano/estado; a função do DataProvider não é estável
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextChargePlanId]);

  // Efeito para carregar o histórico de pagamentos
  useEffect(() => {
    const fetchPayments = () => {
      setLoading(prev => ({ ...prev, payments: true }));
      
      GetSubscriptionPaymentHistory(paymentsPage, 10, (result) => {
        setLoading(prev => ({ ...prev, payments: false }));
        
        if (result.success) {
          setPayments(result.payments);
          setPaymentsPagination(result.pagination);
        } else {
          
        }
      });
    };
    
    fetchPayments();
  // Funções do DataProvider mudam a cada render (value recriado): depender delas
  // refazia as chamadas em loop. Só refaz quando muda a página.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsPage]);
  
  // Efeito para carregar o histórico de alterações
  useEffect(() => {
    const fetchHistory = () => {
      setLoading(prev => ({ ...prev, history: true }));
      
      GetSubscriptionHistory(historyPage, 10, (result) => {
        setLoading(prev => ({ ...prev, history: false }));
        
        if (result.success) {
          setHistory(result.history);
          setHistoryPagination(result.pagination);
        } else {
          
        }
      });
    };
    
    fetchHistory();
  // Funções do DataProvider mudam a cada render (value recriado): depender delas
  // refazia as chamadas em loop. Só refaz quando muda a página.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPage]);
  
  // Efeito para carregar os planos
  useEffect(() => {
    const fetchPlans = () => {
      setLoading(prev => ({ ...prev, plans: true }));
      
      GetSubscriptionPlans((result) => {
        setLoading(prev => ({ ...prev, plans: false }));
        
        if (result.success) {
          // Criar um objeto com os planos indexados pelo ID
          const plansMap = {};
          result.plans.forEach(plan => {
            plansMap[plan.id] = plan;
          });
          setPlans(plansMap);
        } else {
          
        }
      });
    };
    
    fetchPlans();
  // Só ao montar (mesmo motivo dos efeitos acima)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Filtrar e ordenar pagamentos por status e data
  const filteredPayments = useMemo(() => {
    // Primeiro filtramos por status
    let filtered = statusFilter === 'all'
      ? [...payments]
      : payments.filter(payment =>
          payment.status?.toLowerCase() === statusFilter.toLowerCase()
        );
    
    // Depois ordenamos por data (do mais recente para o mais antigo)
    // Tentamos usar paid_date se disponível, senão usamos due_date
    return filtered.sort((a, b) => {
      const dateA = a.paid_date || a.due_date || '';
      const dateB = b.paid_date || b.due_date || '';
      
      // Ordenação decrescente (mais recente primeiro)
      return new Date(dateB) - new Date(dateA);
    });
  }, [payments, statusFilter]);
  
  // Get subscription context functions
  const { invalidateCache, refreshSubscription } = useSubscription();
  
  // Função para cancelar a assinatura principal
  const cancelSubscription = (callback, cancelReason = null, cancelReasonDetails = null) => {
    CancelCompanySubscription((result) => {
      // Invalidar o cache da assinatura após cancelamento
      invalidateCache();
      
      // Forçar atualização dos dados da assinatura
      refreshSubscription(true);
      
      // Recarregar os dados da assinatura após o cancelamento, independente do resultado
      GetCompanySubscription((subscriptionResult) => {
        if (subscriptionResult.success) {
          const subscription = subscriptionResult.subscription;
          setSubscription(subscription);
          setAddons(subscriptionResult.addons || []);
          
          // Se o status da assinatura for "canceling", considerar como sucesso
          if (subscription && subscription.subscription_status?.toLowerCase() === 'canceling') {
            result.success = true;
          }
        }
        
        // Chamar o callback com o resultado
        if (callback) callback(result);
      });
    }, cancelReason, cancelReasonDetails);
  };
  
  // Função para cancelar uma assinatura específica (plano principal ou add-on)
  const cancelSpecificSubscription = (subscriptionId, callback) => {
    if (!subscriptionId) {
      if (callback) callback({ success: false, message: 'ID da assinatura não fornecido' });
      return;
    }
    
    CancelSpecificSubscription(subscriptionId, (result) => {
      if (result.success) {
        // Recarregar os dados da assinatura após o cancelamento
        GetCompanySubscription((subscriptionResult) => {
          if (subscriptionResult.success) {
            setSubscription(subscriptionResult.subscription);
            setAddons(subscriptionResult.addons || []);
          }
        });
      }
      
      // Chamar o callback com o resultado
      if (callback) callback(result);
    });
  };
  
  // Função para gerenciar a assinatura no Stripe
  const manageStripeSubscription = (callback) => {
    if (!subscription || !subscription.stripe_subscription_id) {
      if (callback) callback({ success: false, message: 'Assinatura Stripe não encontrada' });
      return;
    }
    
    CreateStripeCustomerPortal((result) => {
      if (callback) callback(result);
      
      if (result.success && result.url) {
        // Redirecionar para o portal do cliente Stripe
        window.location.href = result.url;
      }
    });
  };
  
  // Retornar os dados e funções
  return {
    subscription,
    addons,
    payments: filteredPayments,
    history,
    plans,
    nextCharge,
    paymentsPagination,
    historyPagination,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    setPaymentsPage,
    setHistoryPage,
    cancelSubscription,
    cancelSpecificSubscription,
    manageStripeSubscription,
    isLoading: Object.values(loading).some(Boolean)
  };
};

export default useSubscriptionData;